import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CheckIcon, FilterIcon, SearchIcon, XIcon } from '../icons'
import './ColumnFilter.css'

/**
 * Tablo sütun başlığı ve filtresi (1 Kas 2026, kullanıcı): başlığın yanında
 * filtre simgesi, basınca altında küçük pencere. Pencerenin üstünde arama
 * kutusu: seçenekli sütunda seçenekleri süzer, metin sütununda (İşletme)
 * doğrudan satırları arar. Filtre uygulanmışsa simge turuncu.
 *
 * Eskiden başlıkların altında ikinci bir satırda arama kutusu ve açılır
 * listeler vardı; kullanıcı "çok kötü duruyor" dedi.
 */

export interface FilterOption {
  value: string
  label: string
}

interface Props {
  label: string
  /** Seçenekli süzgeç: '' "Hepsi". */
  value?: string
  options?: FilterOption[]
  onChange?: (value: string) => void
  /** Metin araması (seçeneksiz sütun). */
  query?: string
  onQuery?: (q: string) => void
  placeholder?: string
}

const fold = (s: string) => s.toLocaleLowerCase('tr')

export function ColumnFilter({ label, value = '', options, onChange, query, onQuery, placeholder }: Props) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)
  const [find, setFind] = useState('')
  const btn = useRef<HTMLButtonElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const textMode = !options
  const active = textMode ? !!query?.trim() : value !== ''
  const W = 260

  const place = () => {
    const r = btn.current?.getBoundingClientRect()
    if (!r) return
    const left = Math.max(8, Math.min(r.left - 12, window.innerWidth - W - 8))
    setPos({ left, top: r.bottom + 8 })
  }

  useLayoutEffect(() => {
    if (!open) return
    const re = () => place()
    window.addEventListener('resize', re)
    window.addEventListener('scroll', re, true)
    return () => {
      window.removeEventListener('resize', re)
      window.removeEventListener('scroll', re, true)
    }
  })

  useEffect(() => {
    if (!open) return
    input.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setOpen(false)
        btn.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open])

  const toggle = () => {
    if (open) return setOpen(false)
    setFind('')
    place()
    setOpen(true)
  }

  const pick = (v: string) => {
    onChange?.(v)
    setOpen(false)
  }

  const all: FilterOption[] = options ? [{ value: '', label: 'Hepsi' }, ...options] : []
  const shown = find.trim() ? all.filter((o) => o.value === '' || fold(o.label).includes(fold(find.trim()))) : all
  const current = options?.find((o) => o.value === value)

  return (
    <span className="colf">
      <span className="colf__label">{label}</span>
      <button
        ref={btn}
        type="button"
        className={`colf__btn ${active ? 'is-on' : ''} ${open ? 'is-open' : ''}`}
        aria-label={`${label} filtresi${active ? `: ${textMode ? query : current?.label}` : ''}`}
        aria-expanded={open}
        title={active ? (textMode ? `"${query}"` : current?.label) : `${label} filtrele`}
        onClick={(e) => {
          e.stopPropagation()
          toggle()
        }}
      >
        <FilterIcon />
        {active && <i aria-hidden="true" />}
      </button>

      {open &&
        pos &&
        createPortal(
          <>
            <div className="colf__scrim" onMouseDown={() => setOpen(false)} />
            <div className="colf__pop" role="dialog" aria-label={`${label} filtresi`} style={{ left: pos.left, top: pos.top, width: W }}>
              <label className="colf__search">
                <SearchIcon />
                <input
                  ref={input}
                  type="search"
                  value={textMode ? query ?? '' : find}
                  placeholder={textMode ? placeholder ?? 'Ara' : `${label} ara`}
                  onChange={(e) => (textMode ? onQuery?.(e.target.value) : setFind(e.target.value))}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    if (textMode) setOpen(false)
                    else {
                      // Aramada tek seçenek kaldıysa Enter onu seçer.
                      const rest = shown.filter((o) => o.value !== '')
                      if (rest.length === 1) pick(rest[0].value)
                    }
                  }}
                />
                {textMode && query && (
                  <button type="button" className="colf__clear" aria-label="Aramayı temizle" onClick={() => onQuery?.('')}>
                    <XIcon />
                  </button>
                )}
              </label>

              {!textMode && (
                <ul className="colf__list" role="listbox" aria-label={label}>
                  {shown.map((o) => (
                    <li key={o.value || '__all'}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={o.value === value}
                        className={o.value === value ? 'is-on' : ''}
                        onClick={() => pick(o.value)}
                      >
                        <span className="colf__tick">{o.value === value && <CheckIcon />}</span>
                        {o.label}
                      </button>
                    </li>
                  ))}
                  {shown.length === 1 && <li className="colf__none">Eşleşen yok</li>}
                </ul>
              )}
            </div>
          </>,
          document.body,
        )}
    </span>
  )
}
